import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import moment from "moment";
import styled from "styled-components";
import DateTimePicker from "react-datetime-picker";
import calendarIcon from "../../images/dashboard/Calendar_icon.png";
import "./calendarStyle.css";
import { useTranslation } from "react-i18next";
import { JobBoardDashboardList } from "./JobBoardDashboardList";
import {
  getJobBoardDashboard,
  setJobBoardDashboardFilters,
} from "./dashboard.redux";
import {
  UnorderedListOutlined,
  AppstoreOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Button as ButtonIcon } from "antd";
import ProgressBar from "react-bootstrap/ProgressBar";
import { Select } from "antd";
import { jobHandleForm, jobNotifyCustomer } from "../arsenal/arsenal.redux";
import feedbackTheme from "../../theme/feedback";

const { color, radius, spacing } = feedbackTheme;

const Page = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md} ${spacing.xl};

  @media only screen and (max-width: 991px) {
    padding: ${spacing.md} ${spacing.sm} ${spacing.lg};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.md};
  gap: ${spacing.md};

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing.sm};
  }
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${color.primary};
  margin: 0;
  letter-spacing: -0.2px;

  @media only screen and (max-width: 767px) {
    font-size: 18px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const FilterCard = styled.div`
  position: relative;
  background: ${color.surface};
  border: 1px solid #e5e7eb;
  border-radius: ${radius.lg};
  padding: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 64, 0.06);
  margin-bottom: ${spacing.md};
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px 10px;
  align-items: end;

  @media only screen and (max-width: 1199px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media only screen and (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: 479px) {
    grid-template-columns: 1fr;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FilterLabel = styled.label`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${color.textMuted};
  margin: 0;
  padding-left: 2px;
`;

const DateTimePickerInput = styled(DateTimePicker)`
  width: 100%;

  & .react-datetime-picker__wrapper {
    border: 1px solid ${color.border};
    border-radius: ${radius.md};
    background: ${color.surface};
    padding: 0 6px;
    height: 32px;
  }

  & .react-datetime-picker__inputGroup {
    flex-grow: 0;
    min-width: 0;
  }

  & .react-datetime-picker__inputGroup__input,
  & .react-datetime-picker__inputGroup__leadingZero,
  & .react-datetime-picker__inputGroup__divider {
    color: ${color.text};
    font-size: 12px;
  }

  & .react-datetime-picker__clear-button,
  & .react-datetime-picker__calendar-button {
    padding: 2px 4px;
  }

  & .react-datetime-picker__clear-button svg {
    width: 14px;
    height: 14px;
    stroke: ${color.textMuted};
  }

  & .react-datetime-picker__calendar-button img {
    width: 14px;
    height: 14px;
  }
`;

const IconBtn = styled(ButtonIcon)`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.md};
  color: ${color.primary};

  &:hover,
  &:focus {
    background: ${color.surfaceAlt};
    border-color: ${color.primary};
    color: ${color.primary};
  }

  & span {
    font-size: 16px;
  }

  & .ant-btn-loading-icon {
    display: inline-flex;
    align-items: center;
  }
`;

const SelectArsenal = styled(Select)`
  width: 100%;

  & .ant-select-selector {
    min-height: 32px !important;
    padding: 0 8px !important;
    border-radius: ${radius.md} !important;
    border-color: ${color.border} !important;
    font-size: 12px;
  }

  & .ant-select-selection-placeholder,
  & .ant-select-selection-item {
    line-height: 30px !important;
    font-size: 12px;
  }

  &.ant-select-multiple .ant-select-selector {
    padding: 2px 6px !important;
  }

  &.ant-select-multiple .ant-select-selection-item {
    height: 22px;
    line-height: 20px !important;
    margin: 2px 4px 2px 0;
    padding: 0 6px;
    background: ${color.surfaceAlt};
    border: none;
    border-radius: 999px;
    color: ${color.primary};
    font-weight: 600;
    font-size: 11px;
  }

  &.ant-select-multiple .ant-select-selection-item-remove {
    color: ${color.primary};
    font-size: 10px;
    margin-left: 4px;
  }

  & .ant-select-selection-search-input {
    height: 28px !important;
    font-size: 12px;
  }
`;

const ProgressSliver = styled(ProgressBar)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  background: transparent;

  & .progress-bar {
    background-color: ${color.primary} !important;
    transition: width 1s linear;
  }
`;

const JobBoardDashboardScreen = ({
  job_board_dashboard_original,
  startingDate,
  endingDate,
  loadingJobBoardDashboard,
  getJobBoardDashboard,
  plugin_settings,
  jobServices,
  setJobBoardDashboardFilters,
  deviceInfo,
  userId,
  isCompleted,
  salesPersons,
  locations,
  jobHandleForm,
  jobNotifyCustomer,
}) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState([
    "pending",
    "late",
    "waiting_pickup",
  ]);
  const [locationFilter, setLocationFilter] = useState([]);
  const [staffFilter, setStaffFilter] = useState([]);
  const [serviceFilter, setServiceFilter] = useState("");
  const { isPortrait } = deviceInfo;
  const [isList, setIsList] = useState(!isPortrait);
  const [timerProgress, setTimerProgress] = useState(
    plugin_settings.job_board_timer
  );
  const refreshTimerRef = useRef();
  const progressTimerRef = useRef();
  const timerProgressRef = useRef(plugin_settings.job_board_timer);

  const refreshJobBoardDashboard = () => {
    if (!loadingJobBoardDashboard) {
      getJobBoardDashboard({});
      timerProgressRef.current = plugin_settings.job_board_timer;
    }
  };

  const onChange = (eDate) => {
    setJobBoardDashboardFilters({
      startingDate: moment().subtract(100, "years").startOf("day").toDate(),
      endingDate: eDate
        ? moment(eDate).endOf("day").toDate()
        : moment().add(100, "years").endOf("day").toDate(),
    });
  };

  let job_board_dashboard = job_board_dashboard_original;

  const handleServiceFilter = (services) => {
    setServiceFilter(services);
    setJobBoardDashboardFilters({
      jobService: (services || []).join(","),
    });
  };

  const handleStatusFilter = (statuses) => {
    setStatusFilter(statuses);
    setJobBoardDashboardFilters({
      status: (statuses || []).join(","),
    });
  };

  const handleLocationFilter = (locations) => {
    setLocationFilter(locations);
    setJobBoardDashboardFilters({
      jobLocation: (locations || []).join(","),
    });
  };

  const handleStaffFilter = (staffs) => {
    setStaffFilter(staffs);
    setJobBoardDashboardFilters({
      salesPerson: (staffs || []).join(","),
    });
  };

  useEffect(() => {
    if (!loadingJobBoardDashboard) {
      getJobBoardDashboard({});
    }
  }, [startingDate, endingDate, statusFilter, locationFilter, staffFilter, serviceFilter]);

  useEffect(() => {
    const timer_time = plugin_settings.job_board_timer;

    if (timer_time > 0) {
      refreshTimerRef.current = setInterval(() => {
        refreshJobBoardDashboard();
      }, timer_time * 1000);

      progressTimerRef.current = setInterval(() => {
        timerProgressRef.current =
          timerProgressRef.current > 0
            ? timerProgressRef.current - 1
            : timer_time;
        // console.log(timerProgressRef.current);
        setTimerProgress(timerProgressRef.current);
      }, 1000);

      return () => {
        clearInterval(refreshTimerRef.current);
        clearInterval(progressTimerRef.current);
      };
    }
  }, []);

  return (
    <Page>
      <HeaderRow>
        <Title>{t("jobBoard.title")}</Title>
        <HeaderActions>
          {!isPortrait && (
            <IconBtn
              type="text"
              aria-label={isList ? t("Grid") : t("List")}
              icon={isList ? <AppstoreOutlined /> : <UnorderedListOutlined />}
              onClick={() => setIsList(!isList)}
            />
          )}
          <IconBtn
            type="text"
            aria-label={t("Refresh")}
            icon={<UndoOutlined />}
            onClick={refreshJobBoardDashboard}
            loading={loadingJobBoardDashboard}
          />
        </HeaderActions>
      </HeaderRow>

      <FilterCard>
        {plugin_settings.job_board_timer > 0 && (
          <ProgressSliver
            now={(timerProgress * 100) / plugin_settings.job_board_timer}
          />
        )}
        <FilterGrid>
          <FilterField>
            <FilterLabel>{t("Date")}</FilterLabel>
            <DateTimePickerInput
              calendarIcon={<img src={calendarIcon} alt="" />}
              onChange={onChange}
              value={new Date(endingDate)}
              disableClock={true}
              format={"dd MMM yyyy"}
              monthAriaLabel="Month"
            />
          </FilterField>
          <FilterField>
            <FilterLabel>{t("Status")}</FilterLabel>
            <SelectArsenal
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={statusFilter}
              options={[
                { value: "pending", label: t("jobBoard.pending") },
                { value: "complete", label: t("jobBoard.completed") },
                { value: "late", label: t("jobBoard.late") },
                { value: "waiting_pickup", label: t("jobBoard.waiting_pickup") },
              ]}
              onChange={handleStatusFilter}
            />
          </FilterField>
          <FilterField>
            <FilterLabel>{t("services")}</FilterLabel>
            <SelectArsenal
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              options={(jobServices || []).map((service) => ({
                value: service.name,
                label: service.name,
              }))}
              onChange={handleServiceFilter}
            />
          </FilterField>
          <FilterField>
            <FilterLabel>{t("jobBoard.location")}</FilterLabel>
            <SelectArsenal
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={locationFilter}
              options={(locations || []).map((location) => ({
                value: location.name,
                label: location.name,
              }))}
              onChange={handleLocationFilter}
            />
          </FilterField>
          <FilterField>
            <FilterLabel>{t("form.label.SalesPerson")}</FilterLabel>
            <SelectArsenal
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={staffFilter}
              options={(salesPersons || []).map((person) => ({
                value: person.name,
                label: person.name,
              }))}
              onChange={handleStaffFilter}
            />
          </FilterField>
        </FilterGrid>
      </FilterCard>

      <JobBoardDashboardList
        jobs={job_board_dashboard}
        loadingCustomer={loadingJobBoardDashboard}
        plugin_settings={plugin_settings}
        jobServices={jobServices}
        statusFilter={statusFilter}
        locationFilter={locationFilter}
        staffFilter={staffFilter}
        isList={isList}
        userId={userId}
        isCompleted={isCompleted}
        jobHandleForm={jobHandleForm}
        jobNotifyCustomer={jobNotifyCustomer}
        refreshJobBoardDashboard={refreshJobBoardDashboard}
      />
    </Page>
  );
};

export default connect(
  (state) => ({
    job_board_dashboard_original: state.dashboard.job_board_dashboard || [],
    startingDate: state.dashboard.jobBoardDashboardFilters.startingDate,
    endingDate: state.dashboard.jobBoardDashboardFilters.endingDate,
    loadingJobBoardDashboard: state.dashboard.loadingJobBoardDashboard,
    userId: state.userSession.userData.id,
    plugin_settings: state.plugin.plugin_settings,
    jobServices: state.plugin.jobServices,
    deviceInfo: state.userSession.deviceInfo,
    isCompleted: state.balls.currentJob?.status === "complete",
    locations: state.plugin.locations,
    salesPersons: state.plugin.salesPersons,
  }),
  {
    getJobBoardDashboard,
    setJobBoardDashboardFilters,
    jobHandleForm,
    jobNotifyCustomer,
  }
)(JobBoardDashboardScreen);
